import React, { useState, useEffect, useCallback } from 'react';
import { authFetchInit, getAuthHeaders, hasAuthSession } from '../utils/authSession';
import { listingTableSx } from '../components/listingTableSx';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Autocomplete,
  CircularProgress,
  Snackbar,
  Alert,
  Tooltip,
  Divider,
  Stack,
  TextField,
} from '@mui/material';
import { Edit as EditIcon, Sync as SyncIcon } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { chipSxFromSurface, getRiskSurface } from '../utils/appearanceTokens';
import { API_ROOT } from '../config/apiConfig';

// ─── Types ────────────────────────────────────────────────────────────────────

type RiskLevel = 'Low' | 'Medium' | 'High' | 'Critical';

interface ScoreThreshold {
  risk_level: RiskLevel;
  score_min: number;
  score_max: number | null; // null = unbounded
}

interface SecurityPolicy {
  id: string;
  name: string;
  type: string;
  status: string;
  idp_source?: string;
}

interface PolicyMapping {
  risk_level: string;
  policy_id: string;
  policy_name: string;
  idp_source?: string;
}

function idpSourceLabel(source?: string): string {
  if (source === 'azure') return 'Entra';
  if (source === 'okta') return 'Okta';
  return 'IdP';
}

const isEntraPolicy = (policy: SecurityPolicy): boolean => policy.idp_source === 'azure';

function normalizePolicySelection(next: SecurityPolicy[]): SecurityPolicy[] {
  const entra = next.filter(isEntraPolicy);
  const others = next.filter((p) => !isEntraPolicy(p));
  if (others.length <= 1) return [...entra, ...others];
  return [...entra, others[others.length - 1]];
}

// ─── Chip colour helper ────────────────────────────────────────────────────────

const RISK_LEVELS: RiskLevel[] = ['Low', 'Medium', 'High', 'Critical'];

const RiskLevelChip: React.FC<{ level: RiskLevel }> = ({ level }) => {
  const { resolvedMode } = useTheme();
  const surface = getRiskSurface(level, resolvedMode);
  return (
    <Chip
      label={level}
      size="small"
      sx={{ ...chipSxFromSurface(surface), fontWeight: 700, fontSize: '0.78rem' }}
    />
  );
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatRange(t: ScoreThreshold): string {
  return t.score_max === null ? `${t.score_min}+` : `${t.score_min} – ${t.score_max}`;
}

// ─── Main Component ────────────────────────────────────────────────────────────

async function parseApiError(res: Response, fallback: string): Promise<string> {
  try {
    const data = await res.json();
    return data.message || data.error || fallback;
  } catch {
    return fallback;
  }
}

async function parseApiJson<T>(res: Response): Promise<T> {
  try {
    return (await res.json()) as T;
  } catch {
    return {} as T;
  }
}

const RiskPolicies: React.FC = () => {
  const { user } = useAuth();
  const canEditThresholds = user?.role === 'super_admin' || user?.role === 'grc_admin';
  const canManageMappings =
    user?.role === 'super_admin' || user?.role === 'sso_admin' || user?.role === 'grc_admin';
  const canSyncPolicies = canManageMappings;
  const canViewPage =
    canEditThresholds || canManageMappings || user?.role === 'iga_admin';

  // Data
  const [thresholds, setThresholds] = useState<ScoreThreshold[]>([]);
  const [policies, setPolicies] = useState<SecurityPolicy[]>([]);
  const [mappings, setMappings] = useState<PolicyMapping[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);

  // Dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingLevel, setEditingLevel] = useState<RiskLevel | null>(null);
  const [editScoreMin, setEditScoreMin] = useState('');
  const [editScoreMax, setEditScoreMax] = useState('');
  const [selectedPolicies, setSelectedPolicies] = useState<SecurityPolicy[]>([]);
  const [saving, setSaving] = useState(false);

  // Snackbar
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>(
    { open: false, message: '', severity: 'success' }
  );

  // ─── Helpers ─────────────────────────────────────────────────────────────────

  const authHeaders = (): HeadersInit => getAuthHeaders({ 'Content-Type': 'application/json' });

  const showSnackbar = (message: string, severity: 'success' | 'error') =>
    setSnackbar({ open: true, message, severity });

  const getMappingsForLevel = (level: RiskLevel) =>
    mappings.filter((m) => m.risk_level.toLowerCase() === level.toLowerCase());

  const getThresholdForLevel = (level: RiskLevel) =>
    thresholds.find((t) => t.risk_level === level);

  // ─── Data fetching ───────────────────────────────────────────────────────────

  const fetchThresholds = useCallback(async (): Promise<string | null> => {
    if (!canEditThresholds) return null;
    try {
      const res = await fetch(`${API_ROOT}/risk-assessment/admin/thresholds`, { headers: authHeaders() });
      const data = await parseApiJson<{ success?: boolean; thresholds?: ScoreThreshold[] }>(res);
      if (!res.ok || !data.success) {
        return await parseApiError(res, `Failed to load score thresholds (${res.status})`);
      }
      if (data.thresholds) setThresholds(data.thresholds);
      return null;
    } catch (err) {
      console.error('Failed to fetch thresholds:', err);
      return 'Failed to load score thresholds.';
    }
  }, [canEditThresholds, user?.role]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchPolicies = useCallback(async (): Promise<string | null> => {
    if (!canViewPage) return null;
    try {
      const res = await fetch(`${API_ROOT}/okta/synced-policies`, { headers: authHeaders() });
      const data = await parseApiJson<{ success?: boolean; data?: SecurityPolicy[] }>(res);
      if (!res.ok) {
        if (res.status === 403) return null;
        return await parseApiError(res, `Failed to load policies (${res.status})`);
      }
      if (data.success && Array.isArray(data.data)) {
        setPolicies(
          data.data.map((p: SecurityPolicy & { id: number | string }) => ({
            ...p,
            id: String(p.id),
          }))
        );
      }
      return null;
    } catch (err) {
      console.error('Failed to fetch policies:', err);
      return 'Failed to load synced policies.';
    }
  }, [canViewPage]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchMappings = useCallback(async (): Promise<string | null> => {
    if (!canViewPage) return null;
    try {
      const res = await fetch(`${API_ROOT}/okta/policy-mappings`, { headers: authHeaders() });
      const data = await parseApiJson<{ success?: boolean; mappings?: PolicyMapping[] }>(res);
      if (!res.ok) {
        if (res.status === 403) return null;
        return await parseApiError(res, `Failed to load policy mappings (${res.status})`);
      }
      if (data.success && data.mappings) setMappings(data.mappings);
      return null;
    } catch (err) {
      console.error('Failed to fetch policy mappings:', err);
      return 'Failed to load policy mappings.';
    }
  }, [canViewPage]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      setLoadError(null);
      const errors = (
        await Promise.all([fetchThresholds(), fetchPolicies(), fetchMappings()])
      ).filter((e): e is string => Boolean(e));
      if (errors.length) setLoadError(errors[0]);
      setLoading(false);
    };
    loadAll();
  }, [fetchThresholds, fetchPolicies, fetchMappings]);

  // ─── Sync policies ────────────────────────────────────────────────────────────

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await fetch(`${API_ROOT}/okta/sync-policies`, { method: 'POST', headers: authHeaders() });
      const data = await parseApiJson<{ success?: boolean; message?: string }>(res);
      if (!res.ok || data.success === false) {
        throw new Error(await parseApiError(res, `Sync failed (${res.status})`));
      }
      showSnackbar(data.message || 'Policies synced successfully.', 'success');
      await fetchPolicies();
    } catch (err) {
      console.error('Sync failed:', err);
      const msg = err instanceof Error ? err.message : 'Failed to sync policies.';
      showSnackbar(msg, 'error');
    } finally {
      setSyncing(false);
    }
  };

  // ─── Edit dialog ─────────────────────────────────────────────────────────────

  const openDialog = (level: RiskLevel) => {
    const threshold = getThresholdForLevel(level);
    const levelMappings = getMappingsForLevel(level);
    const initialPolicies = levelMappings
      .map((m) => policies.find((p) => p.id === String(m.policy_id)))
      .filter((p): p is SecurityPolicy => Boolean(p));
    setEditingLevel(level);
    setEditScoreMin(String(threshold?.score_min ?? ''));
    setEditScoreMax(threshold?.score_max !== null && threshold?.score_max !== undefined ? String(threshold.score_max) : '');
    setSelectedPolicies(initialPolicies);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingLevel(null);
  };

  const handleSave = async () => {
    if (!editingLevel) return;
    setSaving(true);
    try {
      let updatedThresholds: ScoreThreshold[] = [];

      if (canEditThresholds) {
        const scoreMin = parseInt(editScoreMin, 10);
        const scoreMax = editScoreMax.trim() === '' ? null : parseInt(editScoreMax, 10);

        if (isNaN(scoreMin)) {
          showSnackbar('Score Min must be a valid number.', 'error');
          setSaving(false);
          return;
        }

        if (scoreMax !== null && !isNaN(scoreMax) && scoreMax < scoreMin) {
          showSnackbar('Max Score must be greater than or equal to Min Score.', 'error');
          setSaving(false);
          return;
        }

        updatedThresholds = RISK_LEVELS.map((lvl) => {
          if (lvl === editingLevel) return { risk_level: lvl, score_min: scoreMin, score_max: scoreMax };
          const existing = getThresholdForLevel(lvl);
          return existing ?? { risk_level: lvl, score_min: 0, score_max: null };
        });
      }

      if (canEditThresholds) {
        const thrRes = await fetch(`${API_ROOT}/risk-assessment/admin/thresholds`, {
          method: 'PUT',
          headers: authHeaders(),
          body: JSON.stringify({ thresholds: updatedThresholds }),
        });
        const thrData = await parseApiJson<{ success?: boolean }>(thrRes);
        if (!thrRes.ok || thrData.success === false) {
          throw new Error(await parseApiError(thrRes, `Threshold save failed (${thrRes.status})`));
        }
      }

      if (canManageMappings) {
        const mapRes = await fetch(`${API_ROOT}/okta/policy-mappings`, {
          method: 'PUT',
          headers: authHeaders(),
          body: JSON.stringify({
            risk_level: editingLevel,
            policy_ids: selectedPolicies.map((p) => p.id),
          }),
        });
        const mapData = await parseApiJson<{ success?: boolean }>(mapRes);
        if (!mapRes.ok || mapData.success === false) {
          throw new Error(await parseApiError(mapRes, `Policy mapping save failed (${mapRes.status})`));
        }
      } else if (selectedPolicies.length > 0) {
        showSnackbar('You do not have permission to update policy mappings.', 'error');
        closeDialog();
        return;
      }

      if (!canEditThresholds && !canManageMappings) {
        showSnackbar('You do not have permission to save risk settings.', 'error');
        closeDialog();
        return;
      }

      showSnackbar(`${editingLevel} risk settings saved.`, 'success');
      await Promise.all([fetchThresholds(), fetchMappings(), fetchPolicies()]);
      closeDialog();
    } catch (err) {
      console.error('Save failed:', err);
      const msg = err instanceof Error ? err.message : 'Failed to save settings.';
      showSnackbar(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  // ─── Render ──────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      {/* ── Header ── */}
      <Box
        sx={{
          display: 'flex',
          alignItems: { xs: 'flex-start', sm: 'center' },
          justifyContent: 'space-between',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2,
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Risk Policies
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Define risk score thresholds and map security policies. Score % = risk factors present (higher = more risk).
          </Typography>
        </Box>

        {canSyncPolicies && (
          <Tooltip title="Sync available security policies from your identity provider">
            <span>
              <Button
                variant="outlined"
                startIcon={syncing ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />}
                onClick={handleSync}
                disabled={syncing}
                sx={{ whiteSpace: 'nowrap' }}
              >
                {syncing ? 'Syncing…' : 'Sync Policies'}
              </Button>
            </span>
          </Tooltip>
        )}
      </Box>

      {loadError && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setLoadError(null)}>
          {loadError}
        </Alert>
      )}

      {/* ── Table ── */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
          <Table size="medium" sx={listingTableSx}>
            <TableHead>
              <TableRow
                sx={{
                  '& th': {
                    fontWeight: 700,
                    fontSize: '0.8rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                    color: 'text.secondary',
                    bgcolor: 'grey.50',
                  },
                }}
              >
                <TableCell>Risk Level</TableCell>
                <TableCell>Score Range</TableCell>
                <TableCell>Security Policy</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {RISK_LEVELS.map((level, idx) => {
                const threshold = getThresholdForLevel(level);
                const levelMappings = getMappingsForLevel(level);
                return (
                  <TableRow
                    key={level}
                    sx={{
                      bgcolor: idx % 2 === 0 ? 'background.paper' : 'grey.50',
                      '&:last-child td': { borderBottom: 0 },
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    <TableCell sx={{ py: 1.75 }}>
                      <RiskLevelChip level={level} />
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace" fontWeight={500}>
                        {threshold ? formatRange(threshold) : '—'}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      {levelMappings.length > 0 ? (
                        <Stack spacing={0.75}>
                          {levelMappings.map((mapping) => (
                            <Box key={`${mapping.risk_level}-${mapping.policy_id}`}>
                              <Typography variant="body2" fontWeight={500}>
                                {mapping.policy_name}
                              </Typography>
                              {mapping.idp_source && (
                                <Typography variant="caption" color="text.secondary">
                                  {idpSourceLabel(mapping.idp_source)}
                                </Typography>
                              )}
                            </Box>
                          ))}
                        </Stack>
                      ) : (
                        <Typography variant="body2" color="text.disabled" fontStyle="italic">
                          — Not mapped —
                        </Typography>
                      )}
                    </TableCell>

                    <TableCell align="center">
                      <Tooltip title={`Edit ${level} risk settings`}>
                        <IconButton size="small" onClick={() => openDialog(level)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ── Edit Dialog ── */}
      <Dialog
        open={dialogOpen}
        onClose={closeDialog}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          Edit{' '}
          <Box component="span" fontWeight={700}>
            {editingLevel}
          </Box>{' '}
          Risk Settings
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ pt: 2.5, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {editingLevel && <RiskLevelChip level={editingLevel} />}

          {/* Score range inputs */}
          <Box>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Score Range
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
              <TextField
                label="Min Score"
                type="number"
                size="small"
                value={editScoreMin}
                onChange={(e) => setEditScoreMin(e.target.value)}
                sx={{ flex: 1 }}
                inputProps={{ min: 0 }}
                disabled={!canEditThresholds}
              />
              <Typography color="text.secondary">–</Typography>
              <TextField
                label="Max Score"
                type="number"
                size="small"
                value={editScoreMax}
                onChange={(e) => setEditScoreMax(e.target.value)}
                placeholder="∞ (unbounded)"
                sx={{ flex: 1 }}
                inputProps={{ min: 0 }}
                helperText={
                  canEditThresholds ? 'Leave blank for unbounded' : 'Score ranges are managed by GRC admins'
                }
                disabled={!canEditThresholds}
              />
            </Box>
          </Box>

          {/* Policy selector */}
          <Box>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Security Policy
            </Typography>
            <Autocomplete
              multiple
              size="small"
              disabled={!canManageMappings}
              options={policies}
              value={selectedPolicies}
              onChange={(_, value) => {
                const next = Array.isArray(value) ? value : [];
                if (next.length === 0) {
                  setSelectedPolicies([]);
                  return;
                }
                const last = next[next.length - 1];
                if (!isEntraPolicy(last)) {
                  setSelectedPolicies([last]);
                  return;
                }
                setSelectedPolicies(normalizePolicySelection(next));
              }}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              getOptionLabel={(option) => option.name}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      {option.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {idpSourceLabel(option.idp_source)} · {option.type} · {option.status}
                    </Typography>
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Policy"
                  placeholder={
                    selectedPolicies.length === 0 || selectedPolicies.every(isEntraPolicy)
                      ? 'Search Entra policies (multiple allowed)'
                      : 'One Okta / IdP policy per risk level'
                  }
                />
              )}
            />

            {policies.length === 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                No policies available.{' '}
                {canSyncPolicies
                  ? 'Use the Sync Policies button to fetch from your identity provider.'
                  : 'Ask a GRC or SSO admin to sync policies.'}
              </Typography>
            )}
            {policies.some(isEntraPolicy) && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Entra policies support multiple selections. Okta and other IdP policies allow one per risk level.
              </Typography>
            )}
          </Box>
        </DialogContent>

        <Divider />

        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button onClick={closeDialog} disabled={saving} size="small">
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            size="small"
            startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Snackbar ── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RiskPolicies;
